import { useRef, useState } from 'react';
import type { TrackerEntity, TrackerStatus } from '@shared/flow/tracker';
import { TrackerPopover } from './TrackerPopover';
import { useTrackerMutations } from './useTracker';

/**
 * Per-row tracker chip — prototype `trackerChip()`
 * (docs/flow/custom-views-prototype.html:858). `+ Status` when the record
 * has no tracker; otherwise the derived label per the doc's matrix: Due
 * (overdue accent), ★ Starred, Reminder <date>, or Snoozed <date>.
 *
 * `bucket` is the SERVER-computed bucket label (`row.tracker` — the same
 * `TRACKER_BUCKET_SQL` CASE the `groupBy: 'tracker'` group headers use, see
 * src/services/flow/query/tracker-sql.ts) and drives the "is it Due" accent
 * — the chip never re-derives "due" from `Date.now()` client-side, so a
 * clock-skewed browser can't show a different due state than the row's own
 * group header.
 */

export interface TrackerChipProps {
  entity: TrackerEntity;
  recordId: string;
  status: TrackerStatus | null;
  date: string | null;
  note: string | null;
  bucket: string | null;
  /** Record name, forwarded to the popover heading ("Status · <name>"). */
  recordLabel?: string;
}

const BUCKET_CHIP_CLASSES: Record<string, string> = {
  Due: 'border-pomegranate-200 dark:border-pomegranate-800 bg-bg-red-light text-content-danger dark:text-pomegranate-400 font-semibold',
  Starred: 'border-tangerine-200 dark:border-tangerine-800 bg-tangerine-50 dark:bg-tangerine-900/30 text-tangerine-700 dark:text-tangerine-400 font-semibold',
  Reminders: 'border-blueberry-200 dark:border-blueberry-800 bg-bg-blue-light text-content-action dark:text-blueberry-400',
  Snoozed: 'border-secondary dark:border-nightshade-700 bg-bg-secondary dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400',
};

const NO_STATUS_CHIP_CLASSES =
  'border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 text-content-tertiary dark:text-nightshade-500 hover:bg-bg-secondary dark:hover:bg-nightshade-700';

function chipLabel(status: TrackerStatus | null, date: string | null, bucket: string | null): string {
  if (!status) return '+ Status';
  if (bucket === 'Due') return date ? `Due ${date}` : 'Due';
  if (status === 'starred') return '★ Starred';
  if (status === 'reminder') return date ? `Reminder ${date}` : 'Reminder';
  return date ? `Snoozed ${date}` : 'Snoozed';
}

export function TrackerChip({ entity, recordId, status, date, note, bucket, recordLabel }: TrackerChipProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { save, clear, isSaving, isClearing } = useTrackerMutations(entity);

  const label = chipLabel(status, date, bucket);
  const chipClasses = status ? (BUCKET_CHIP_CLASSES[bucket ?? ''] ?? NO_STATUS_CHIP_CLASSES) : NO_STATUS_CHIP_CLASSES;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        aria-expanded={open}
        aria-label={status ? `Tracker status: ${label}` : 'Set tracker status'}
        title={note || undefined}
        className={`inline-flex items-center whitespace-nowrap rounded-md border px-1.5 py-0.5 text-[11px] leading-4 ${chipClasses}`}
      >
        {label}
      </button>
      <TrackerPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        recordId={recordId}
        recordLabel={recordLabel}
        status={status}
        date={date}
        note={note}
        isSaving={isSaving}
        isClearing={isClearing}
        onSave={input => {
          save({ recordId, status: input.status, date: input.date, note: input.note });
          setOpen(false);
        }}
        onClear={() => {
          clear(recordId);
          setOpen(false);
        }}
      />
    </>
  );
}
